import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Marker, Polyline, Popup } from 'react-leaflet';
import { formatedTrackPathtoLatLng, buildTasksArray } from '../../utils/helperFunctions';
import { renderToStaticMarkup } from 'react-dom/server';
import L from 'leaflet';
import { ReactComponent as DeleteIcon } from "../../assets/icons/toursManagement/delete.svg";
import { ReactComponent as UpdateIcon } from "../../assets/icons/toursManagement/update.svg";
import { ReactComponent as MoveIcon } from "../../assets/icons/toursManagement/move.svg";
import { ReactComponent as ClockIcon } from "../../assets/icons/toursManagement/clock.svg";
import { ReactComponent as SmsIcon } from "../../assets/icons/toursManagement/sms.svg";
import { ReactComponent as SoundIcon } from "../../assets/icons/toursManagement/sound.svg";
import { ReactComponent as PoiWrapper } from "../../assets/icons/general/poi_wrapper2.svg";
import common_types from '../../common_files/common_types';
import { Padding } from '@mui/icons-material';

import "./path.css"
const POI_blue_wrapper_Icon = renderToStaticMarkup(<span style={{ color: '#009ddb' }}><PoiWrapper /></span>);
const POI_red_wrapper_Icon = renderToStaticMarkup(<span style={{ color: '#FF3239' }}><PoiWrapper /></span>);
const POI_yellow_wrapper_Icon = renderToStaticMarkup(<span style={{ color: '#FFD700' }}><PoiWrapper /></span>);
const POI_gray_wrapper_Icon = renderToStaticMarkup(<span style={{ color: '#A9A9A9' }}><PoiWrapper /></span>);

const generateIconHtmlContent = (p_text, taskIconsMarkup ="", color = 'blue') => {
    return `
    <div style="display:flex;justify-content:center;align-items:center;position:relative;bottom:25px;">
        <div style="position:absolute;">${color === 'blue' ? POI_blue_wrapper_Icon : (color === 'yellow' ? POI_yellow_wrapper_Icon :(color === 'gray' ? POI_gray_wrapper_Icon:POI_red_wrapper_Icon))}</div>
        <div style="position:relative;bottom:6px;height:25px;width:35px;display:flex;justify-content:center;align-items:center;font-size:16px;color:black;">
            <b>${p_text}</b>
        </div>
        <div style="
            position:absolute; 
            top:-50px; 
            left:50%; /* Position icons relative to the center */
            transform: translateX(-50%); /* Center the icons horizontally */
            display:flex; 
            justify-content:center; 
            align-items:center;
        ">
            ${taskIconsMarkup}
        </div>
    </div>`;
};

function Path({ path, onPathClick, pathIndex, weight = '12', taskHandlers, moveMode, isStart=false, isEnd=false,taskActive=false, isPreview=false,}) {
    const SMS = common_types.task_type.TRIGGERED_TASK_SMS;
    const WAIT = common_types.task_type.TRIGGERED_TASK_WAIT;
    const SOUND = common_types.task_type.TRIGGERED_TASK_PLAY_SOUND;
  
    // Avoid React state for hover to prevent rerenders that can close popups
    const [data, setData] = useState([]);
    const [tasksArray, setTasksArray] = useState([]);
    const polylineRef = useRef(null);
    const popupRef = useRef(null);
    const markerRefs = useRef({}); // Store marker instances by stable key
    const [isTaskActive, setIsTaskActive] = useState(false);

    const [popupContent, setPopupContent] = useState("actions"); // Use this state to control popup content
    const [selectedTaskDetails, setSelectedTaskDetails] = useState(null); // Add this state to store the selected task's details
    // Track which task popup is open using a stable key (pathIndex-pointIndex)
    const [openTaskKey, setOpenTaskKey] = useState(null);

    const baseColor = "#B378FF";
    const hoverColor = "#5D42FF";
    const shuttleColor = "#FF8127";
    const isShuttle = path.path_type && path.path_type.includes("SHUTTLE") && path.path?.current_poi_location !== "cart_drive_to_start_point";

    // Support external UI meta decoration (color + dashed) provided by shuttle preview components
    const decoratedColor = path?.ui_meta?.color;
    const isDashed = !!path?.ui_meta?.dashed;

    // Choose final color (respect decoration first)
    const finalColor = decoratedColor || (isShuttle ? shuttleColor : baseColor);

    // Optional hover variant (slight darken) when decorated
    const finalHoverColor = decoratedColor ? hoverColorFor(finalColor) : hoverColor;

    function hoverColorFor(hex){
        if(!/^#?[0-9a-fA-F]{6}$/.test(hex)) return hoverColor;
        const h = hex.replace('#','');
        const r = Math.max(0, parseInt(h.substring(0,2),16)-30);
        const g = Math.max(0, parseInt(h.substring(2,4),16)-30);
        const b = Math.max(0, parseInt(h.substring(4,6),16)-30);
        return `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`;
    }

    // Effect to handle updating path data, tasks, and store task details directly in tasksArray
    useEffect(() => {
        
        const relevantData = formatedTrackPathtoLatLng(path.path.points);
        setData(relevantData);

        const currentPathTasks = buildTasksArray([path]);

        if (Array.isArray(currentPathTasks) && currentPathTasks.length > 0) {
            const tasksWithIconsAndDetails = currentPathTasks.map((task) => ({
                ...task,
                icons: task.tasks.map((subTask) => getTaskIcon(subTask)), // Attach icons based on sub-tasks
                details: task.tasks.map((subTask) => {
                    // Generate task details ahead of time
                    switch (subTask.task_type) {
                        case "SmsTask":
                            return `SMS Task:\nMessage: ${subTask.data.text}\nPhone Numbers: ${subTask.data.phone_numbers_list.join(', ')}\nTimeout: ${subTask.data.timeout_seconds} seconds`;
                        case "WaitTask":
                            return `Wait Task:\nWait Time: ${subTask.data.wait_time_seconds} seconds`;
                        case "PlaySoundTask":
                            if (Array.isArray(subTask.data.sounds_playlist) && subTask.data.sounds_playlist.length > 0) {
                                const lines = subTask.data.file_name.concat('\n').concat(subTask.data.sounds_playlist.map((s, i) => `${i+1}. ${s.file_name} (${s.duration || 0}s)`).join('\n'));
                                return `Play Sound Task\nPlaylist:\n${lines}\nWait Until End: ${subTask.data.wait_until_sound_ended ? 'Yes' : 'No'}`;
                            }
                            return `Play Sound Task\nSound File: ${subTask.data.file_name}\nWait Until End: ${subTask.data.wait_until_sound_ended ? 'Yes' : 'No'}`;
                        default:
                            return 'Unknown Task';
                    }
                }).join('\n\n') // Join task details for readability
            }));

            setTasksArray(tasksWithIconsAndDetails); // Store tasks with icons and pre-generated details
        } else {
            setTasksArray([]); // Reset the tasks array if no tasks are present
        }
    }, [JSON.stringify(path)]); // Re-run if the path data changes

    // If task mode deactivates, ensure any open popup closes
    useEffect(() => {
        if (!isTaskActive && openTaskKey) {
            setOpenTaskKey(null);
        }
    }, [isTaskActive, openTaskKey]);

    // When openTaskKey changes, programmatically open/close the correct popup
    useEffect(() => {
        const refs = markerRefs.current || {};
        Object.entries(refs).forEach(([key, marker]) => {
            if (!marker) return;
            try {
                if (key === String(openTaskKey)) {
                    marker.openPopup();
                } else {
                    marker.closePopup();
                }
            } catch (e) {
                // ignore leaflet instance errors
            }
        });
    }, [openTaskKey]);

    const getTaskIcon = (task) => {
        switch (task.task_type) {
            case SMS:
                return <SmsIcon key={task.id} />;
            case WAIT:
                return <ClockIcon key={task.id} />;
            case SOUND:
                return <SoundIcon key={task.id} />;
            default:
                return null;
        }
    };

    

    // Memoize task icons markup for performance optimization
    const taskIconsMarkup = useMemo(() => {
        return tasksArray.map((taskGroup) => {
            return taskGroup.icons.map((icon, index) => (
                `<div key=${index} style="
                    margin: 0 5px;
                    background-color: rgba(255, 255, 255, 0.5); /* Transparent white */
                    border-radius: 50%; /* Make the background circular */
                    width: 30px; /* Set the size of the circle */
                    height: 30px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                ">
                    ${renderToStaticMarkup(icon)}
                </div>`
            )).join('');
        });
    }, [tasksArray]);

    // Handle polyline rendering in front
    useEffect(() => {
        if (polylineRef.current) {
            polylineRef.current.bringToFront(); // Bring the path to the front
        }
        setIsTaskActive(taskActive);
    }, [weight,taskActive]);


    return (
        <>
        <Polyline
                ref={polylineRef}
                weight={weight}
                pathOptions={{
            color: finalColor,
                    lineCap: 'round',
                    lineJoin: 'miter',
            dashArray: isDashed ? '6 6' : undefined,
                }}
                positions={data}
                eventHandlers={{
                    click: (e) => {
                        if (typeof onPathClick === 'function') {
                            onPathClick(e.latlng, path.path.points, pathIndex);
                        }
                    },
                    mouseover: () => {
                        if (polylineRef.current) {
                            polylineRef.current.bringToFront(); // Bring to front on hover
                            polylineRef.current.setStyle({ color: finalHoverColor });
                        }
                    },
                    mouseout: () => {
                        if (polylineRef.current) {
                            polylineRef.current.setStyle({ color: finalColor });
                        }
                    },
                    onAdd: () => {
                        polylineRef.current.bringToFront();
                    }
                }}
            />
            {/* shuttle start icon */}
            {   isStart && Array.isArray(data) && data.length > 0 && 
                data[0] && Array.isArray(data[0]) && data[0].length >= 2 &&
                !isNaN(data[0][0]) && !isNaN(data[0][1]) && (
                <Marker 
                    position={data[0]} 
                    icon={L.divIcon({
                        className: "my-custom-pin",
                        html: generateIconHtmlContent("S","",'yellow')
                    })
                } 
                />
                )
            }
            {/* shuttle end icon */}
            {   isEnd && Array.isArray(data) && data.length > 0 && 
                data[data.length -1] && Array.isArray(data[data.length -1]) && data[data.length -1].length >= 2 &&
                !isNaN(data[data.length -1][0]) && !isNaN(data[data.length -1][1]) && (
                <Marker 
                    position={data[data.length -1]} 
                    icon={L.divIcon({
                            className: "my-custom-pin",
                            html: generateIconHtmlContent("E","",'yellow')
                        })
                    } 
                />
                )
            }
            {/* Task markers */}
            {tasksArray.map((taskGroup, index) => {
                // Validate task coordinates before rendering
                const taskX = taskGroup.point?.point?.x;
                const taskY = taskGroup.point?.point?.y;
                
                if (!taskX || !taskY || isNaN(parseFloat(taskX)) || isNaN(parseFloat(taskY))) {
                    console.warn(`Invalid task coordinates for task ${index}:`, taskGroup);
                    return null;
                }
                
                const taskPosition = [parseFloat(taskY), parseFloat(taskX)];
                console.log(`Rendering task ${index} at position:`, taskPosition);
                // Stable key for this marker/popup
                const markerKey = `${pathIndex}-${taskGroup.pointIndex}`;
                
                return (
             (isTaskActive || isPreview ? (<Marker
                 key={markerKey}
                 ref={(instance) => {
                     if (instance) {
                         markerRefs.current[markerKey] = instance;
                     } else {
                         delete markerRefs.current[markerKey];
                     }
                 }}
                 position={taskPosition} // Use validated task position
                 icon={L.divIcon({
                     className: "my-custom-pin",
                     html: generateIconHtmlContent(
                         "T",
                         taskIconsMarkup[index], // Use the memoized task icons markup
                         (moveMode && moveMode.oldPathIndex === pathIndex && moveMode.pointIndex === taskGroup.pointIndex ? 'red' : 'blue') // Apply red color for move mode
                     )                
                 })}
                 eventHandlers={{
                     click: () => {
                         if (!isTaskActive) return;
                         setOpenTaskKey((prev) => prev === markerKey ? null : markerKey);
                     },
                     popupclose: () => {
                         setOpenTaskKey(null);
                         setPopupContent("actions"); // Reset to actions when popup is closed
                         setSelectedTaskDetails(null); // Clear task details when closing
                     }
                 }}
             >
                <Popup autoClose={false} closeOnClick={false} autoPan={false} keepInView={false} ref={popupRef} position={"top-center"} offset={[0, -20]}>
                     <div style={{ 
                         textAlign: 'center', 
                         color: 'white', 
                         maxWidth: '300px', 
                         wordWrap: 'break-word',
                         padding: '10px', // Padding for better spacing
                         borderRadius: '10px',// Rounded corners for a cleaner look
                         backgroundColor: 'rgba(0,0,0,0.8)',
                         minWidth:'120px'
                     }}>
                         <div style={{ marginBottom: '10px', textAlign: 'left', whiteSpace: 'pre-wrap' }}>
                             <b>Task Details:</b>
                             <p>{taskGroup.details}</p>
                         </div>
                         {taskHandlers &&
                         <div style={{ display: 'flex', justifyContent: 'center', maxWidth: '300px'}}>
                             <button title="Delete Task" style={{padding:'0px'}} onClick={() => taskHandlers.onDelete(taskGroup.pointIndex,pathIndex)}><DeleteIcon  style={{ width: '30px', height: '30px', fill: '#FFFFFF' }}/></button>
                             <button title="Move Task" style={{padding:'0px'}} onClick={() => taskHandlers.onMove(taskGroup.pointIndex, pathIndex)}><MoveIcon style={{ width: '30px', height: '30px', fill: '#FFFFFF' }}/></button>
                             <button title="Edit Task" style={{padding:'0px'}} onClick={() => taskHandlers.onEdit(taskGroup.pointIndex, pathIndex)}><UpdateIcon  style={{ width: '30px', height: '30px', fill: '#FFFFFF' }}/></button>
                         </div>}
                     </div>
                 </Popup>
             </Marker>) : "")
                );
            }).filter(Boolean)} {/* Filter out null values from invalid coordinates */}
        </>
    );
}

export default React.memo(Path);
